import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Tag,
  Percent,
  Calendar,
  Home,
  ChevronRight,
  Loader2,
  Package,
  ArrowRight,
} from "lucide-react";
import WebsiteHeader from "./WebsiteHeader";
import WebsiteFooter from "./WebsiteFooter";

const API = process.env.REACT_APP_BACKEND_URL;

const WebsitePromotions = () => {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPromotions = async () => {
      try {
        const response = await axios.get(`${API}/api/website/promotions`);
        setPromotions(response.data.promotions || []);
      } catch (error) {
        console.error("Error fetching promotions:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPromotions();
  }, []);

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-ZA", {
      style: "currency",
      currency: "ZAR",
    }).format(price || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      return new Date(dateStr).toLocaleDateString("en-ZA", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <WebsiteHeader />
      
      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center gap-2 text-sm">
            <Link to="/site" className="text-gray-500 hover:text-blue-600 flex items-center gap-1">
              <Home className="h-4 w-4" />
              Home
            </Link>
            <ChevronRight className="h-4 w-4 text-gray-400" />
            <span className="text-gray-800 font-medium">Specials & Promotions</span>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-gradient-to-r from-red-600 to-orange-500 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <Badge className="bg-yellow-400 text-black mb-4 text-lg px-4 py-1">
            <Percent className="h-4 w-4 mr-1" />
            HOT DEALS
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Specials & Promotions</h1>
          <p className="text-xl text-red-100 max-w-2xl mx-auto">
            Discover amazing deals on quality hardware, tools, and building materials. 
            Limited time offers - do not miss out!
          </p>
        </div>
      </section>

      <div className="container mx-auto px-4 py-12 flex-1">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : promotions.length === 0 ? (
          <div className="text-center py-20">
            <Tag className="h-16 w-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-2xl font-semibold text-gray-700 mb-2">No Active Promotions</h3>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">
              We are currently preparing new specials for you. Check back soon or visit our store for in-person deals!
            </p>
            <Link to="/site/products">
              <Button className="bg-blue-600 hover:bg-blue-700">
                Browse All Products
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {promotions.map((promo, index) => (
              <Card key={promo.id || index} className="overflow-hidden hover:shadow-xl transition-shadow">
                <div className="bg-gradient-to-r from-red-500 to-orange-400 text-white p-4">
                  <div className="flex justify-between items-start">
                    <Badge className="bg-yellow-400 text-black">
                      {promo.discount_percentage ? `${promo.discount_percentage}% OFF` : "SPECIAL"}
                    </Badge>
                    {promo.end_date && (
                      <div className="text-xs flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        Until {formatDate(promo.end_date)}
                      </div>
                    )}
                  </div>
                  <h2 className="text-xl font-bold mt-3">{promo.name}</h2>
                </div>
                
                <CardContent className="p-4">
                  {promo.description && (
                    <p className="text-gray-600 mb-4">{promo.description}</p>
                  )}
                  
                  {promo.products && promo.products.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="font-semibold text-sm text-gray-700">Products on Special:</h4>
                      {promo.products.slice(0, 5).map((item, i) => (
                        <div key={i} className="flex items-center gap-3 p-2 bg-gray-50 rounded">
                          <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center flex-shrink-0">
                            {item.image_url ? (
                              <img src={item.image_url} alt="" className="w-full h-full object-cover rounded" />
                            ) : (
                              <Package className="h-6 w-6 text-gray-400" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{item.product_name || "Product"}</p>
                            <div className="flex items-center gap-2">
                              {item.original_price && (
                                <span className="text-xs text-gray-400 line-through">
                                  {formatPrice(item.original_price)}
                                </span>
                              )}
                              {item.promotional_price && (
                                <span className="text-sm font-bold text-red-600">
                                  {formatPrice(item.promotional_price)}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      {promo.products.length > 5 && (
                        <p className="text-sm text-gray-500 text-center">
                          +{promo.products.length - 5} more products
                        </p>
                      )}
                    </div>
                  )}
                  
                  <div className="mt-4 pt-4 border-t">
                    <Link to="/site/contact">
                      <Button variant="outline" className="w-full">
                        Enquire About This Deal
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* CTA Section */}
        <div className="mt-16 bg-slate-900 rounded-2xl p-8 md:p-12 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Can not find what you are looking for?</h2>
          <p className="text-gray-300 mb-6 max-w-xl mx-auto">
            Contact us for custom quotes on bulk orders or special requests. 
            We are here to help with all your hardware and building needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/site/contact">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700">
                Get a Quote
              </Button>
            </Link>
            <a href="tel:0832785937">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                Call 083 278 5937
              </Button>
            </a>
          </div>
        </div>
      </div>

      <WebsiteFooter />
    </div>
  );
};

export default WebsitePromotions;
